import { Constants, userApiScope } from "@/services/web/api.const";
import { CreateNewUser, FilterParams, PagedResult, UpdateUser, User } from "../users.type";
import { useApi, useApiAsync } from "@/services/web/use-api";

import { RoleType, Roles } from "@/store/auth/auth.type";
import { mergeParam } from "@/helper/api";

export const useGetUsers = (
  role: RoleType,
  filter: FilterParams,
  enabled = false,
): { loading: boolean; error?: Error; users?: PagedResult<User>; refetch: () => void } => {
  const options = { method: "GET", ...userApiScope };

  // The management API now exposes a single `/users` endpoint that takes
  // `roles`, `cities`, `blocked`, and the usual pagination/search filters
  // as query params. The role-in-path variants (`/users/:roleName/users`)
  // were removed.
  const queryParams: Record<string, string | number> = {
    ...(filter as Record<string, string | number>),
    ...(role !== Roles.ALL ? { roles: role } : {}),
  };
  const url = mergeParam(Constants.MANAGEMENT_API_URL + "/users", queryParams);

  const {
    isLoading: loading,
    error,
    data: users,
    refetch,
  } = useApi<PagedResult<User>>(url, { ...options }, ["users", role, filter], enabled);

  return { loading, error, users, refetch };
};

export const useGetUser = (
  userId: string | undefined,
): { loading?: boolean; error?: Error; user?: User; refetch: () => void } => {
  const options = {
    method: "GET",
    ...userApiScope,
  };

  const url = Constants.MANAGEMENT_API_URL + "/users/" + userId;
  const { isLoading: loading = false, error, data: user, refetch } = useApi<User>(url, options, ["user", userId]);

  return {
    loading,
    error,
    user,
    refetch,
  };
};

export const useCreateUser = () => {
  const url = Constants.MANAGEMENT_API_URL + "/users/create";

  const options = {
    ...userApiScope,
    method: "POST",
    headers: { "Content-type": "application/json; charset=UTF-8" },
  };

  const {
    mutateAsync: createUser,
    isPending: loading,
    error,
  } = useApiAsync<void, CreateNewUser>(url, options, ["users"]);

  return {
    createUser,
    loading,
    error,
  };
};

export const useUpdateUser = (userId: string) => {
  const url = `${Constants.MANAGEMENT_API_URL}/users/${userId}`;

  const options = {
    ...userApiScope,
    method: "PUT",
    headers: {
      "Content-type": "application/json; charset=UTF-8",
    },
  };

  const {
    mutateAsync: updateUser,
    isPending: loading,
    error,
  } = useApiAsync<void, UpdateUser>(url, options, ["user", userId]);

  return {
    updateUser,
    loading,
    error,
  };
};

export const useDeleteUser = (userId: string) => {
  const url = Constants.MANAGEMENT_API_URL + "/users/" + userId;

  const options = {
    ...userApiScope,
    url: Constants.MANAGEMENT_API_URL + "/users/" + userId,
    method: "DELETE",
  };

  const { mutateAsync: deleteUser, isPending: loading, error } = useApiAsync(url, options, ["users"]);

  return {
    deleteUser,
    loading,
    error,
  };
};

export const useDownloadUsersCvsByRole = (roleName: RoleType | undefined) => {
  // Generic `/users/export` endpoint, scoped via the same `roles` query
  // param the list endpoint uses. The CSV's field separator is derived
  // server-side from the request's Accept-Language header.
  const url = roleName ? mergeParam(Constants.MANAGEMENT_API_URL + "/users/export", { roles: roleName }) : "";

  const options = {
    ...userApiScope,
    method: "GET",
  };

  const { mutateAsync: fetchUsersCSV, isPending: loading, error } = useApiAsync<string>(url, options);

  return {
    fetchUsersCSV,
    loading,
    error,
  };
};
